'use client';

import { cn } from '@/app/lib';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Input, Spinner, Typography } from '@workspace/ui/common';
import { CustomScrollBar } from '@workspace/ui/common/CustomScrollBar/CustomScrollBar';

type Suggestion = {
  id: string;
  primary: string;
  secondary?: string;
  full: string;
  latitude: number;
  longitude: number;
};

type AuPostLocality = {
  category: string;
  id: number;
  latitude: number;
  location: string;
  longitude: number;
  postcode: string;
  state: string;
};

type AuPostRes = {
  localities?: {
    locality?: Array<AuPostLocality> | AuPostLocality;
  };
};

export type PlacesSearchBoxProps = {
  containerClassName?: string;
  placeholder?: string;
  value?: string;
  onCenterSelected: (coords: { lat: number; lng: number }, formattedAddress: string) => void;
  onValueChange?: (value: string) => void;
  fullWidth?: boolean;
  onReady?: (geocoder: google.maps.Geocoder) => void;
};

const AUSTRALIAN_STATES = /\b(NSW|VIC|QLD|SA|WA|TAS|NT|ACT)\b/g;
const POSTCODE_REGEX = /\b(\d{4})\b/; // Australian postcode format
const DEBOUNCE_DELAY = 300;

const formatAddress = (primaryText: string): string => {
  const postcodeMatch = primaryText.match(POSTCODE_REGEX);
  const postalCode = postcodeMatch?.[1] ?? '';

  const locality = primaryText
    .replace(AUSTRALIAN_STATES, '')
    .replace(POSTCODE_REGEX, '')
    .replace(/,?\s*Australia\s*/g, '')
    .replace(/,+/g, '')
    .trim();

  if (locality && postalCode) return `${locality}, ${postalCode}`;
  return locality || postalCode || primaryText;
};

const capitalizeLocation = (location?: string): string => {
  return location?.toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase()) ?? '';
};

export function PlacesSearchBox({
  containerClassName,
  placeholder,
  value: externalValue,
  onCenterSelected,
  onValueChange,
  fullWidth = false,
}: PlacesSearchBoxProps) {
  const [value, setValue] = useState('');
  const [open, setOpen] = useState(false);
  const [predictions, setPredictions] = useState<Suggestion[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const justSelectedRef = useRef(false);
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (externalValue === undefined) return;
    setValue(externalValue);
    if (!externalValue) {
      setPredictions([]);
      setOpen(false);
    }
  }, [externalValue]);

  const handleClickOutside = useCallback((event: MouseEvent) => {
    if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
      setOpen(false);
    }
  }, []);

  useEffect(() => {
    if (open) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [open, handleClickOutside]);

  useEffect(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // Australia Post API requires at least 3 characters to return suggestions
    if (!value || value.length < 3) {
      setPredictions([]);
      setOpen(false);
      setIsLoading(false);
      return;
    }

    if (justSelectedRef.current) {
      justSelectedRef.current = false;
      return;
    }

    let cancelled = false;

    const fetchSuggestions = async () => {
      try {
        setIsLoading(true);
        const response = await fetch(`/api/auspost-proxy?q=${value}`);

        if (!response.ok) {
          throw new Error(`Australia Post API error: ${response.statusText}`);
        }

        const data: AuPostRes = await response.json();
        if (cancelled) return;
        const allItems: Suggestion[] = [];
        if (data.localities?.locality) {
          const localities = Array.isArray(data.localities.locality)
            ? data.localities.locality
            : [data.localities.locality];
          for (const loc of localities) {
            // Check required fields
            if (!loc.location || !loc.postcode || !loc.latitude || !loc.longitude) continue;
            const location = capitalizeLocation(loc.location);
            const locationState = loc.state ? `${location} ${loc.state}` : `${location} ${loc.state}`;
            const full = `${locationState} ${loc.postcode}, Australia`;
            allItems.push({
              id: `aupost-${loc.id}`,
              primary: full,
              secondary: `${locationState}, Australia`,
              full,
              latitude: loc.latitude,
              longitude: loc.longitude,
            });
          }
        }

        setPredictions(allItems);
        setOpen(allItems.length > 0);
      } catch (error) {
        console.warn('Error fetching autocomplete suggestions', error);
      } finally {
        setIsLoading(false);
      }
    };

    debounceTimerRef.current = setTimeout(() => {
      fetchSuggestions();
    }, DEBOUNCE_DELAY);

    return () => {
      cancelled = true;
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [value]);

  const setChoose = useCallback(
    ({ location, address }: { location: { lat: number; lng: number }; address: string }) => {
      const formatted = formatAddress(address);
      onCenterSelected(location, formatted);
      setPredictions([]);
      setOpen(false);
      justSelectedRef.current = true;
    },
    [onCenterSelected],
  );

  const choose = useCallback(
    async (s: Suggestion) => {
      setChoose({
        location: { lat: s.latitude, lng: s.longitude },
        address: s.primary,
      });
    },
    [setChoose],
  );

  const handleChange = useCallback(
    (v: string) => {
      setValue(v);
      onValueChange?.(v);
    },
    [onValueChange],
  );

  const handleFocus = useCallback(() => {
    if (predictions.length > 0) {
      setOpen(true);
    }
  }, [predictions.length]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' && predictions.length > 0) {
        e.preventDefault();
        choose(predictions[0]);
      }
    },
    [predictions, choose],
  );

  return (
    <div ref={containerRef} className={cn('relative', fullWidth ? 'w-full' : 'w-122', containerClassName)}>
      <Input
        value={value}
        onChange={handleChange}
        onFocus={handleFocus}
        onKeyDown={handleKeyDown}
        placeholder={placeholder || 'Enter your suburb or postcode'}
        variant="field"
        iconRight="SearchFilter"
        iconSize={24}
        className="w-full! max-w-full! h-12! font-body placeholder:text-base placeholder:font-normal placeholder:text-secondary-200"
      />
      {(open || isLoading) && (
        <div className="absolute z-20 mt-1 w-full bg-white border border-secondary-100 rounded-sm shadow-sm">
          <CustomScrollBar maxHeight="max-h-44" orientation="vertical">
            <div>
              {isLoading ? (
                <div className="w-full flex items-center justify-center gap-2 text-center px-3 py-4">
                  <Spinner size="small" className="text-primary" />
                  <Typography variant="bodySm">Loading...</Typography>
                </div>
              ) : (
                predictions.map((p) => (
                  <button
                    key={p.id}
                    type="button"
                    className="w-full text-left px-3 py-2 hover:bg-secondary-50"
                    onClick={() => choose(p)}
                  >
                    <div className="text-sm text-black">{p.primary}</div>
                    {p.secondary ? <div className="text-xs text-secondary-400">{p.secondary}</div> : null}
                  </button>
                ))
              )}
            </div>
          </CustomScrollBar>
        </div>
      )}
    </div>
  );
}
